/**
 * Excel-facing semantic routing contract for `finster_query`.
 *
 * The native Excel agent chooses stable public categories or capabilities;
 * this module resolves them to the existing data-feed `data_sources` API.
 * Internal data-agent function names and parameters deliberately remain hidden:
 * the nested agent owns those implementation details. Keeping the public
 * vocabulary here also prevents provider schemas, runtime routing, and tests
 * from drifting independently.
 */

import type { FinsterQueryDataSource } from "../shared/api/finster/constants";

/** Shared metadata required to describe and resolve one public routing entry. */
interface ResearchCategoryDefinition {
  /** Concise model-facing explanation of when the route is appropriate. */
  description: string;
  /** Existing data-feed source codes enabled by the route. */
  dataSources: readonly FinsterQueryDataSource[];
}

/** A selectable exact capability and its normal semantic category. */
interface ResearchCapabilityDefinition extends ResearchCategoryDefinition {
  /** Category used for discoverability; exact capability routing takes precedence. */
  category: FinsterQueryCategory;
}

/**
 * Broad routing choices shown to the Excel agent.
 *
 * Categories are the normal/default selection mechanism. Each category expands
 * to a bounded set of data-feed sources; data-feed remains authoritative for
 * user entitlements and feature-gated availability.
 */
export const FINSTER_QUERY_CATEGORY_CATALOG = {
  company_facts: {
    description:
      "Reported financials, company profiles, segments, executives, people, private-company information, classifications, and registry facts.",
    dataSources: [
      "FACTSET_FUNDAMENTALS",
      "FACTSET_BUSINESS_GEO_SEGMENTS",
      "FACTSET_PEOPLE",
      "FACTSET_PRIVATE_COMPANY",
      "FACTSET_RBICS",
      "COMPANY_PROFILE",
      "COMPANIES_HOUSE",
    ],
  },
  market_numbers: {
    description:
      "Prices, market statistics, consensus estimates, guidance, ratings, earnings dates, benchmarks, ETFs, and economic events.",
    dataSources: [
      "MARKET_DATA",
      "ESTIMATES",
      "EARNINGS_BEAT_MISS",
      "EARNINGS_CALENDAR",
      "FACTSET_ECONOMIC_EVENTS",
      "FACTSET_INDEX_BENCHMARK",
      "FACTSET_ETF",
    ],
  },
  documents: {
    description:
      "SEC filings, global filings, annual reports, investor-relations documents, corporate presentations, and transcripts.",
    dataSources: [
      "SEC_FILINGS",
      "INVESTOR_RELATIONS_DOCUMENTS",
      "FACTSET_GLOBAL_FILINGS",
      "EARNING_AND_INVESTOR_CALLS",
    ],
  },
  news_web_events: {
    description:
      "Current web information, official company websites, industry research, news, and event coverage.",
    dataSources: [
      "WEB_SEARCH",
      "COMPANY_WEBSITE",
      "INDUSTRY_RESEARCH",
      "FACTSET_STREETACCOUNT_NEWS",
    ],
  },
  ownership_deals_funds_credit: {
    description:
      "Ownership, fund holdings, M&A, private markets, debt capital structure, and bond terms.",
    dataSources: [
      "FACTSET_OWNERSHIP",
      "FACTSET_MA_DEALS",
      "PRIVATE_MARKETS_DATA",
      "FACTSET_DCS",
      "FACTSET_TC",
    ],
  },
  screening: {
    description:
      "Constructing a new company population/list from given user conditions. Screening is expensive and must not be used for known company lists.",
    dataSources: ["ALL_COMPANY_SEARCH", "FACTSET_RBICS", "PRIVATE_MARKETS_DATA"],
  },
  broker_research: {
    description: "Entitled sell-side broker research and report synthesis.",
    dataSources: ["FACTSET_AMR"],
  },
} as const satisfies Record<string, ResearchCategoryDefinition>;

/** Stable public category names accepted by the Excel tool schema. */
export type FinsterQueryCategory = keyof typeof FINSTER_QUERY_CATEGORY_CATALOG;

/**
 * Precise model-facing capabilities available as category overrides.
 *
 * Names describe user intent rather than backend implementation. One public
 * capability may map to a source that exposes several cooperating internal
 * tools (for example SEC list/retrieve); the nested data agent selects and
 * parameterizes those tools.
 */
export const FINSTER_QUERY_TOOL_CATALOG = {
  reported_financials: {
    category: "company_facts",
    description:
      "Reported historical income-statement, balance-sheet, cash-flow, and fundamental financial data.",
    dataSources: ["FACTSET_FUNDAMENTALS"],
  },
  reported_segment_financials: {
    category: "company_facts",
    description: "Reported business-segment and geographic-segment financial actuals.",
    dataSources: ["FACTSET_BUSINESS_GEO_SEGMENTS"],
  },
  company_profile: {
    category: "company_facts",
    description:
      "Company background including industry, leadership, employees, headquarters, and market capitalization.",
    dataSources: ["COMPANY_PROFILE"],
  },
  company_executives: {
    category: "company_facts",
    description: "Executives and key personnel associated with named companies.",
    dataSources: ["FACTSET_PEOPLE"],
  },
  person_profile: {
    category: "company_facts",
    description: "Professional profile and employment history for a named individual.",
    dataSources: ["FACTSET_PEOPLE"],
  },
  private_company_profile: {
    category: "company_facts",
    description: "Profile and reference information for a private company.",
    dataSources: ["FACTSET_PRIVATE_COMPANY"],
  },
  private_company_financial_statements: {
    category: "company_facts",
    description: "Available financial statements for a private company.",
    dataSources: ["FACTSET_PRIVATE_COMPANY"],
  },
  private_company_financial_metrics: {
    category: "company_facts",
    description: "Available financial metrics for a private company.",
    dataSources: ["FACTSET_PRIVATE_COMPANY"],
  },
  company_industry_classification: {
    category: "company_facts",
    description: "A company's primary FactSet RBICS industry classification.",
    dataSources: ["FACTSET_RBICS"],
  },
  company_industry_revenue_exposure: {
    category: "company_facts",
    description: "A company's revenue exposure across FactSet RBICS industries.",
    dataSources: ["FACTSET_RBICS"],
  },
  industry_taxonomy: {
    category: "company_facts",
    description: "Browse or resolve the FactSet RBICS industry taxonomy.",
    dataSources: ["FACTSET_RBICS"],
  },
  product_brand_classification: {
    category: "company_facts",
    description: "Map company products and brand trade names to FactSet RBICS industries.",
    dataSources: ["FACTSET_RBICS"],
  },
  uk_company_registry: {
    category: "company_facts",
    description: "Live UK Companies House registration and company facts.",
    dataSources: ["COMPANIES_HOUSE"],
  },
  price_and_market_history: {
    category: "market_numbers",
    description:
      "Historical prices, volume, shares, market capitalization, and related market statistics.",
    dataSources: ["MARKET_DATA"],
  },
  consensus_estimates: {
    category: "market_numbers",
    description: "Rolling or aggregate FactSet consensus estimates.",
    dataSources: ["ESTIMATES"],
  },
  fixed_period_consensus: {
    category: "market_numbers",
    description: "Consensus estimates for specified fiscal periods and as-of dates.",
    dataSources: ["ESTIMATES"],
  },
  consensus_actuals: {
    category: "market_numbers",
    description:
      "Actual values reported through the estimates dataset rather than statement fundamentals.",
    dataSources: ["ESTIMATES"],
  },
  consensus_forecast_report: {
    category: "market_numbers",
    description: "A structured forecast report for one company and estimate type.",
    dataSources: ["ESTIMATES"],
  },
  earnings_surprises: {
    category: "market_numbers",
    description: "Reported results compared with consensus, including beat and miss history.",
    dataSources: ["EARNINGS_BEAT_MISS"],
  },
  management_guidance: {
    category: "market_numbers",
    description: "Company-issued management guidance and guidance ranges.",
    dataSources: ["ESTIMATES"],
  },
  analyst_ratings: {
    category: "market_numbers",
    description: "Analyst ratings, ratings distribution, and ratings trends.",
    dataSources: ["ESTIMATES"],
  },
  broker_estimates: {
    category: "market_numbers",
    description: "Estimates attributed to individual brokers or analysts.",
    dataSources: ["ESTIMATES"],
  },
  segment_estimates: {
    category: "market_numbers",
    description: "Forward estimates by reported business segment and broker.",
    dataSources: ["ESTIMATES"],
  },
  segment_estimate_actuals: {
    category: "market_numbers",
    description: "Actual segment values available through the estimates dataset.",
    dataSources: ["ESTIMATES"],
  },
  earnings_calendar: {
    category: "market_numbers",
    description: "Upcoming company earnings announcement dates.",
    dataSources: ["EARNINGS_CALENDAR"],
  },
  index_benchmark_snapshot: {
    category: "market_numbers",
    description: "Current equity-index benchmark values and statistics.",
    dataSources: ["FACTSET_INDEX_BENCHMARK"],
  },
  index_benchmark_history: {
    category: "market_numbers",
    description: "Historical equity-index benchmark series.",
    dataSources: ["FACTSET_INDEX_BENCHMARK"],
  },
  etf_reference: {
    category: "market_numbers",
    description: "ETF reference information and classifications.",
    dataSources: ["FACTSET_ETF"],
  },
  economic_calendar: {
    category: "market_numbers",
    description: "Macroeconomic releases such as CPI, GDP, and employment reports.",
    dataSources: ["FACTSET_ECONOMIC_EVENTS"],
  },
  debt_summary: {
    category: "ownership_deals_funds_credit",
    description: "Latest high-level debt totals and capital-structure summary.",
    dataSources: ["FACTSET_DCS"],
  },
  debt_totals: {
    category: "ownership_deals_funds_credit",
    description: "Debt totals and debt-by-type breakdowns.",
    dataSources: ["FACTSET_DCS"],
  },
  debt_liquidity: {
    category: "ownership_deals_funds_credit",
    description: "Liquidity, revolvers, credit facilities, and undrawn borrowing capacity.",
    dataSources: ["FACTSET_DCS"],
  },
  debt_instruments: {
    category: "ownership_deals_funds_credit",
    description: "Instrument-level debt stack for a specified reporting date.",
    dataSources: ["FACTSET_DCS"],
  },
  current_debt_instruments: {
    category: "ownership_deals_funds_credit",
    description: "Latest instrument-level debt stack; preferred for current debt detail.",
    dataSources: ["FACTSET_DCS"],
  },
  debt_maturity_profile: {
    category: "ownership_deals_funds_credit",
    description: "Debt amounts by maturity year for maturity-wall analysis.",
    dataSources: ["FACTSET_DCS"],
  },
  bond_terms: {
    category: "ownership_deals_funds_credit",
    description: "Bond inventory and broad issue terms.",
    dataSources: ["FACTSET_TC"],
  },
  bond_issue_size: {
    category: "ownership_deals_funds_credit",
    description: "Original issue size and current outstanding bond amount.",
    dataSources: ["FACTSET_TC"],
  },
  bond_coupon_history: {
    category: "ownership_deals_funds_credit",
    description: "Historical coupon changes and floating-rate reset mechanics.",
    dataSources: ["FACTSET_TC"],
  },
  bond_coupon_schedule: {
    category: "ownership_deals_funds_credit",
    description: "Scheduled and forward bond coupon rates and payment periods.",
    dataSources: ["FACTSET_TC"],
  },
  bond_covenants: {
    category: "ownership_deals_funds_credit",
    description: "Bond covenant clauses, restrictions, and financial tests.",
    dataSources: ["FACTSET_TC"],
  },
  bond_redemption_schedule: {
    category: "ownership_deals_funds_credit",
    description:
      "Call, put, and sinking-fund redemption dates and prices; not the issuer's overall debt maturity wall.",
    dataSources: ["FACTSET_TC"],
  },
  bond_agents: {
    category: "ownership_deals_funds_credit",
    description: "Trustees, paying agents, guarantors, and other bond-issue parties.",
    dataSources: ["FACTSET_TC"],
  },
  bond_underwriters: {
    category: "ownership_deals_funds_credit",
    description: "Lead underwriters associated with bond issuance.",
    dataSources: ["FACTSET_TC"],
  },
  bond_use_of_proceeds: {
    category: "ownership_deals_funds_credit",
    description: "The issuer's stated use of proceeds for a bond issuance.",
    dataSources: ["FACTSET_TC"],
  },
  sec_filings: {
    category: "documents",
    description:
      "List and retrieve citeable SEC evidence, including 10-K, 10-Q, 8-K, and proxy filings.",
    dataSources: ["SEC_FILINGS"],
  },
  earnings_transcripts: {
    category: "documents",
    description:
      "List and retrieve citeable earnings-call and investor-event transcript evidence.",
    dataSources: ["EARNING_AND_INVESTOR_CALLS"],
  },
  global_filings: {
    category: "documents",
    description: "Retrieve non-US and global regulatory filings.",
    dataSources: ["FACTSET_GLOBAL_FILINGS"],
  },
  investor_relations_documents: {
    category: "documents",
    description:
      "Retrieve annual reports, investor presentations, and other company IR evidence.",
    dataSources: ["INVESTOR_RELATIONS_DOCUMENTS"],
  },
  web_search: {
    category: "news_web_events",
    description:
      "General current web and news retrieval when a targeted authoritative source is unavailable.",
    dataSources: ["WEB_SEARCH"],
  },
  company_website: {
    category: "news_web_events",
    description: "Retrieve information specifically from official company websites.",
    dataSources: ["COMPANY_WEBSITE"],
  },
  industry_research: {
    category: "news_web_events",
    description: "Retrieve industry, market-intelligence, and government research.",
    dataSources: ["INDUSTRY_RESEARCH"],
  },
  streetaccount_news: {
    category: "news_web_events",
    description: "Retrieve FactSet StreetAccount headlines and short-horizon news.",
    dataSources: ["FACTSET_STREETACCOUNT_NEWS"],
  },
  fund_holdings: {
    category: "ownership_deals_funds_credit",
    description: "Securities held by a named fund or ETF.",
    dataSources: ["FACTSET_OWNERSHIP"],
  },
  security_holders: {
    category: "ownership_deals_funds_credit",
    description: "Institutions and funds holding a named security.",
    dataSources: ["FACTSET_OWNERSHIP"],
  },
  ownership_transactions: {
    category: "ownership_deals_funds_credit",
    description: "Institutional or insider ownership purchase and sale activity.",
    dataSources: ["FACTSET_OWNERSHIP"],
  },
  ma_deals: {
    category: "ownership_deals_funds_credit",
    description: "M&A transactions involving named companies.",
    dataSources: ["FACTSET_MA_DEALS"],
  },
  private_markets_research: {
    category: "ownership_deals_funds_credit",
    description: "Private-company, investor, startup, and funding ecosystem information.",
    dataSources: ["PRIVATE_MARKETS_DATA"],
  },
  private_markets_deal_screening: {
    category: "screening",
    description:
      "Construct a population of private-market funding, M&A, or IPO transactions from user conditions.",
    dataSources: ["PRIVATE_MARKETS_DATA"],
  },
  company_screening: {
    category: "screening",
    description:
      "Construct a new company population/list from given user conditions. Expensive and slow; never use for a known company list or single-company research.",
    dataSources: ["ALL_COMPANY_SEARCH"],
  },
  industry_peer_screening: {
    category: "screening",
    description: "Construct a peer population from a specified primary RBICS industry.",
    dataSources: ["FACTSET_RBICS"],
  },
  industry_revenue_screening: {
    category: "screening",
    description: "Construct a company population from RBICS industry revenue exposure.",
    dataSources: ["FACTSET_RBICS"],
  },
  broker_research: {
    category: "broker_research",
    description: "Retrieve and synthesize entitled sell-side broker research reports.",
    dataSources: ["FACTSET_AMR"],
  },
} as const satisfies Record<string, ResearchCapabilityDefinition>;

/** Stable public capability names accepted by `tool_names`. */
export type FinsterQueryCapability = keyof typeof FINSTER_QUERY_TOOL_CATALOG;

const categoryGuidance = Object.entries(FINSTER_QUERY_CATEGORY_CATALOG)
  .map(([name, definition]) => `${name}: ${definition.description}`)
  .join("\n");

const toolGuidance = Object.entries(FINSTER_QUERY_TOOL_CATALOG)
  .map(([name, definition]) => `${name}: ${definition.description}`)
  .join("\n");

/**
 * Strict provider schema exposed by Excel's `finster_query` tool.
 *
 * All fields are required for constrained decoding. `tool_names: []` selects
 * category routing; any non-empty `tool_names` array overrides categories.
 * Query text must be self-contained because the nested research agent cannot
 * inspect the workbook or the owning agent's conversation.
 */
export const EXCEL_FINSTER_QUERY_INPUT_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    query: {
      type: "string",
      description:
        "A complete, targeted, standalone knowledge-work request. Include every company, identifier, period, metric, unit, premise, and workbook value needed by the research agent, plus the exact required response format. The called agent cannot inspect the workbook, cells, formulas, selected range, conversation, previous calls, or your reasoning. Never refer to 'this workbook', 'this row', 'the selected cells', 'the company above', or 'as previously discussed'.",
    },
    tool_categories: {
      type: "array",
      minItems: 1,
      items: {
        type: "string",
        enum: Object.keys(FINSTER_QUERY_CATEGORY_CATALOG),
      },
      description:
        "DEFAULT ROUTING METHOD. Select the smallest sufficient category set and set tool_names to []. Use categories unless specific named capabilities are required.\n" +
        categoryGuidance,
    },
    tool_names: {
      type: "array",
      items: {
        type: "string",
        enum: Object.keys(FINSTER_QUERY_TOOL_CATALOG),
      },
      description:
        "Normally pass an empty array so tool_categories controls routing. Populate this only when information must come from known, specific capabilities. A non-empty list completely overrides tool_categories. Normal example: tool_categories=['documents'], tool_names=[]. Exact example: tool_categories=['documents'], tool_names=['sec_filings'].\n" +
        toolGuidance,
    },
  },
  required: ["query", "tool_categories", "tool_names"],
} as const;

/**
 * Flatten route definitions into a deterministic de-duplicated source list.
 *
 * @param definitions - Category or capability definitions selected by the model.
 * @returns Data-feed source codes in first-seen catalog order.
 */
function uniqueSources(definitions: readonly ResearchCategoryDefinition[]): FinsterQueryDataSource[] {
  return Array.from(new Set(definitions.flatMap((definition) => definition.dataSources)));
}

/**
 * Validate and normalize one string-array argument from an untrusted tool call.
 *
 * @param value - Raw provider-decoded argument.
 * @param field - Public field name used in validation messages.
 * @returns A de-duplicated trimmed array, or a safe validation-error string.
 */
function readStringArray(value: unknown, field: string): string[] | string {
  if (!Array.isArray(value)) return `${field} must be an array.`;
  if (value.some((entry) => typeof entry !== "string" || !entry.trim())) {
    return `${field} must contain only non-empty strings.`;
  }
  return Array.from(new Set(value.map((entry) => String(entry).trim())));
}

/**
 * Result of resolving model-facing routing arguments.
 *
 * `legacy` preserves query-only callers, `categories` and
 * `specific_capabilities` carry resolved source codes, and `invalid` prevents
 * transport with a model-recoverable error.
 */
export type FinsterQueryRoutingResolution =
  | { mode: "legacy"; dataSources: undefined }
  | {
      mode: "categories" | "specific_capabilities";
      dataSources: FinsterQueryDataSource[];
    }
  | { mode: "invalid"; dataSources: undefined; error: string };

/**
 * Resolve public routing arguments into the existing data-feed source contract.
 *
 * Precedence is deliberate: a non-empty `tool_names` list completely overrides
 * `tool_categories`; an empty list expands the selected categories. Calls that
 * omit both fields are treated as legacy and retain their run-scoped source
 * snapshot. Supplying only one field, unknown values, or an empty category list
 * fails closed.
 *
 * @param rawInput - Parsed, untrusted `finster_query` arguments.
 * @returns A discriminated routing result containing sources or a safe error.
 */
export function resolveFinsterQueryRouting(
  rawInput: Record<string, unknown>,
): FinsterQueryRoutingResolution {
  const hasCategories = Object.prototype.hasOwnProperty.call(rawInput, "tool_categories");
  const hasToolNames = Object.prototype.hasOwnProperty.call(rawInput, "tool_names");
  if (!hasCategories && !hasToolNames) {
    return { mode: "legacy", dataSources: undefined };
  }
  if (!hasCategories || !hasToolNames) {
    return {
      mode: "invalid",
      dataSources: undefined,
      error: "tool_categories and tool_names must be provided together.",
    };
  }

  const categories = readStringArray(rawInput.tool_categories, "tool_categories");
  if (typeof categories === "string") {
    return { mode: "invalid", dataSources: undefined, error: categories };
  }
  const toolNames = readStringArray(rawInput.tool_names, "tool_names");
  if (typeof toolNames === "string") {
    return { mode: "invalid", dataSources: undefined, error: toolNames };
  }
  if (categories.length === 0) {
    return {
      mode: "invalid",
      dataSources: undefined,
      error: "tool_categories must contain at least one category.",
    };
  }

  const unknownCategories = categories.filter(
    (category) => !(category in FINSTER_QUERY_CATEGORY_CATALOG),
  );
  if (unknownCategories.length > 0) {
    return {
      mode: "invalid",
      dataSources: undefined,
      error: `Unknown tool_categories: ${unknownCategories.join(", ")}.`,
    };
  }
  const unknownTools = toolNames.filter((tool) => !(tool in FINSTER_QUERY_TOOL_CATALOG));
  if (unknownTools.length > 0) {
    return {
      mode: "invalid",
      dataSources: undefined,
      error: `Unknown tool_names: ${unknownTools.join(", ")}.`,
    };
  }

  if (toolNames.length > 0) {
    return {
      mode: "specific_capabilities",
      dataSources: uniqueSources(
        toolNames.map(
          (tool) => FINSTER_QUERY_TOOL_CATALOG[tool as FinsterQueryCapability],
        ),
      ),
    };
  }
  return {
    mode: "categories",
    dataSources: uniqueSources(
      categories.map(
        (category) => FINSTER_QUERY_CATEGORY_CATALOG[category as FinsterQueryCategory],
      ),
    ),
  };
}
